import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { PDFDocument } from 'pdf-lib';
import { ArchivoOriginal, Gasto } from '@shared/types';

interface GeneratePDFOptions {
  archivoOriginal?: ArchivoOriginal;
}

/**
 * Genera un PDF del contenido HTML y opcionalmente añade el documento original al final.
 * Esto es útil para auditorías donde se necesita ver el documento fuente.
 */
export async function generatePDF(
  elementId: string,
  filename: string,
  options?: GeneratePDFOptions
): Promise<void> {
  const element = document.getElementById(elementId);
  if (!element) {
    throw new Error('Element not found');
  }

  try {
    // Configurar el elemento para la captura
    const originalWidth = element.style.width;
    const originalMaxWidth = element.style.maxWidth;

    // Asegurar ancho fijo para A4
    element.style.width = '210mm';
    element.style.maxWidth = '210mm';

    // Capturar el elemento como canvas
    const canvas = await html2canvas(element, {
      scale: 2, // Mayor resolución
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      windowWidth: 794, // 210mm en pixels a 96 DPI
    });

    // Restaurar estilos originales
    element.style.width = originalWidth;
    element.style.maxWidth = originalMaxWidth;

    // Calcular dimensiones para PDF con márgenes
    const pageWidth = 210; // A4 width in mm
    const pageHeight = 297; // A4 height in mm
    const margin = 20; // Margen en mm (todos los lados)
    const usableWidth = pageWidth - 2 * margin;
    const usableHeight = pageHeight - 2 * margin;

    // Altura total del contenido escalado al ancho útil
    const imgHeightMM = (canvas.height * usableWidth) / canvas.width;

    const jsPdfDoc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    // Calcular número de páginas y recortar el canvas en trozos
    const totalPages = Math.ceil(imgHeightMM / usableHeight);

    for (let page = 0; page < totalPages; page++) {
      if (page > 0) jsPdfDoc.addPage();

      // Calcular la porción del canvas que corresponde a esta página
      const sliceYPx = Math.round((page * usableHeight / imgHeightMM) * canvas.height);
      const sliceHeightPx = Math.min(
        Math.round((usableHeight / imgHeightMM) * canvas.height),
        canvas.height - sliceYPx
      );

      // Crear un sub-canvas con solo esta porción
      const pageCanvas = document.createElement('canvas');
      pageCanvas.width = canvas.width;
      pageCanvas.height = sliceHeightPx;
      const ctx = pageCanvas.getContext('2d')!;
      ctx.drawImage(canvas, 0, sliceYPx, canvas.width, sliceHeightPx, 0, 0, canvas.width, sliceHeightPx);

      const pageImgData = pageCanvas.toDataURL('image/png');
      const sliceHeightMM = (sliceHeightPx * usableWidth) / canvas.width;

      jsPdfDoc.addImage(pageImgData, 'PNG', margin, margin, usableWidth, sliceHeightMM);
    }

    // Si hay documento original, añadirlo al final
    if (options?.archivoOriginal) {
      const finalPdfBytes = await appendOriginalDocument(jsPdfDoc, options.archivoOriginal);
      downloadBlob(finalPdfBytes, filename);
    } else {
      // Descargar el PDF sin documento original
      jsPdfDoc.save(filename);
    }
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
}

/**
 * Añade el documento original al PDF generado.
 * Soporta imágenes (jpg, png, gif, webp) y PDFs.
 */
async function appendOriginalDocument(
  jsPdfDoc: jsPDF,
  archivoOriginal: ArchivoOriginal
): Promise<Uint8Array> {
  // Convertir jsPDF a bytes para usar con pdf-lib
  const jsPdfBytes = jsPdfDoc.output('arraybuffer');
  const pdfDoc = await PDFDocument.load(jsPdfBytes);

  // Añadir página separadora indicando que viene el documento original
  const separatorPage = pdfDoc.addPage([595.28, 841.89]); // A4 en puntos
  separatorPage.drawText('DOCUMENTO ORIGINAL ADJUNTO', {
    x: 50,
    y: 750,
    size: 18,
  });
  separatorPage.drawText(`Archivo: ${archivoOriginal.nombre}`, {
    x: 50,
    y: 720,
    size: 12,
  });
  separatorPage.drawText(`Fecha de subida: ${new Date(archivoOriginal.fechaSubida).toLocaleDateString('es-ES')}`, {
    x: 50,
    y: 700,
    size: 12,
  });
  separatorPage.drawText(`Tipo: ${archivoOriginal.mimeType}`, {
    x: 50,
    y: 680,
    size: 12,
  });
  separatorPage.drawText('El documento original se muestra a continuación para fines de auditoría.', {
    x: 50,
    y: 640,
    size: 10,
  });

  try {
    // Descargar el archivo original
    const response = await fetch(archivoOriginal.url);
    if (!response.ok) {
      throw new Error(`Error al descargar documento original: ${response.status}`);
    }
    const originalBytes = await response.arrayBuffer();

    const mimeType = archivoOriginal.mimeType.toLowerCase();

    if (mimeType === 'application/pdf') {
      // Si es PDF, copiar todas sus páginas
      const originalPdf = await PDFDocument.load(originalBytes);
      const copiedPages = await pdfDoc.copyPages(originalPdf, originalPdf.getPageIndices());
      copiedPages.forEach(page => pdfDoc.addPage(page));
    } else if (mimeType.startsWith('image/')) {
      // Si es imagen, añadirla en una nueva página
      await addImageToPdf(pdfDoc, originalBytes, mimeType);
    } else {
      console.warn(`Tipo de archivo no soportado para anexar: ${mimeType}`);
    }
  } catch (error) {
    console.error('Error al anexar documento original:', error);
    // En caso de error, añadir nota en la página separadora
    separatorPage.drawText('⚠ No se pudo cargar el documento original.', {
      x: 50,
      y: 600,
      size: 12,
    });
  }

  return pdfDoc.save();
}

/**
 * Añade una imagen al PDF, ajustándola al tamaño de página A4.
 */
async function addImageToPdf(
  pdfDoc: PDFDocument,
  imageBytes: ArrayBuffer,
  mimeType: string
): Promise<void> {
  let image;

  if (mimeType.includes('png')) {
    image = await pdfDoc.embedPng(imageBytes);
  } else if (mimeType.includes('jpeg') || mimeType.includes('jpg')) {
    image = await pdfDoc.embedJpg(imageBytes);
  } else {
    // Para otros formatos (gif, webp), intentar como PNG
    // Nota: pdf-lib no soporta gif/webp directamente, pero podemos intentar
    try {
      image = await pdfDoc.embedPng(imageBytes);
    } catch {
      try {
        image = await pdfDoc.embedJpg(imageBytes);
      } catch {
        console.warn(`No se pudo incrustar imagen con tipo: ${mimeType}`);
        return;
      }
    }
  }

  // Dimensiones A4 en puntos
  const pageWidth = 595.28;
  const pageHeight = 841.89;
  const margin = 40;

  const maxWidth = pageWidth - (margin * 2);
  const maxHeight = pageHeight - (margin * 2);

  // Calcular escala manteniendo proporción
  const imgDims = image.scale(1);
  let scale = Math.min(maxWidth / imgDims.width, maxHeight / imgDims.height);
  if (scale > 1) scale = 1; // No agrandar imágenes pequeñas

  const scaledWidth = imgDims.width * scale;
  const scaledHeight = imgDims.height * scale;

  // Centrar la imagen en la página
  const x = (pageWidth - scaledWidth) / 2;
  const y = (pageHeight - scaledHeight) / 2;

  const page = pdfDoc.addPage([pageWidth, pageHeight]);
  page.drawImage(image, {
    x,
    y,
    width: scaledWidth,
    height: scaledHeight,
  });
}

/**
 * Descarga un blob como archivo.
 */
function downloadBlob(pdfBytes: Uint8Array, filename: string): void {
  const blob = new Blob([pdfBytes as unknown as BlobPart], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Genera un PDF para un gasto, incluyendo el documento original si existe.
 */
export async function generateGastoPDF(gasto: Partial<Gasto>, filename: string): Promise<void> {
  const jsPdfDoc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  // Configuración de estilos
  const pageWidth = 210;
  const margin = 20;
  let y = 30;

  // Título
  jsPdfDoc.setFontSize(22);
  jsPdfDoc.setFont('helvetica', 'bold');
  jsPdfDoc.text('REGISTRO DE GASTO', pageWidth / 2, y, { align: 'center' });
  y += 15;

  // Línea separadora
  jsPdfDoc.setDrawColor(200, 200, 200);
  jsPdfDoc.line(margin, y, pageWidth - margin, y);
  y += 15;

  // Datos del proveedor
  jsPdfDoc.setFontSize(14);
  jsPdfDoc.setFont('helvetica', 'bold');
  jsPdfDoc.text('Proveedor:', margin, y);
  jsPdfDoc.setFont('helvetica', 'normal');
  jsPdfDoc.text(gasto.proveedor || '-', margin + 35, y);
  y += 10;

  jsPdfDoc.setFont('helvetica', 'bold');
  jsPdfDoc.text('CIF/NIF:', margin, y);
  jsPdfDoc.setFont('helvetica', 'normal');
  jsPdfDoc.text(gasto.cif || '-', margin + 35, y);
  y += 15;

  // Datos de la factura
  jsPdfDoc.setFont('helvetica', 'bold');
  jsPdfDoc.text('Nº Factura:', margin, y);
  jsPdfDoc.setFont('helvetica', 'normal');
  jsPdfDoc.text(gasto.numeroFactura || '-', margin + 35, y);

  jsPdfDoc.setFont('helvetica', 'bold');
  jsPdfDoc.text('Fecha:', pageWidth / 2, y);
  jsPdfDoc.setFont('helvetica', 'normal');
  const fechaFormateada = gasto.fecha
    ? new Date(gasto.fecha).toLocaleDateString('es-ES')
    : '-';
  jsPdfDoc.text(fechaFormateada, pageWidth / 2 + 20, y);
  y += 15;

  // Descripción
  if (gasto.descripcion) {
    jsPdfDoc.setFont('helvetica', 'bold');
    jsPdfDoc.text('Descripción:', margin, y);
    y += 7;
    jsPdfDoc.setFont('helvetica', 'normal');
    jsPdfDoc.setFontSize(12);
    const splitDescription = jsPdfDoc.splitTextToSize(gasto.descripcion, pageWidth - margin * 2);
    jsPdfDoc.text(splitDescription, margin, y);
    y += splitDescription.length * 6 + 10;
    jsPdfDoc.setFontSize(14);
  }

  // Línea separadora
  jsPdfDoc.setDrawColor(200, 200, 200);
  jsPdfDoc.line(margin, y, pageWidth - margin, y);
  y += 15;

  // Importes
  const formatCurrency = (value: number | undefined) =>
    (value ?? 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

  jsPdfDoc.setFont('helvetica', 'normal');
  jsPdfDoc.text('Base Imponible:', margin, y);
  jsPdfDoc.text(formatCurrency(gasto.baseImponible), pageWidth - margin, y, { align: 'right' });
  y += 10;

  jsPdfDoc.text('IVA:', margin, y);
  jsPdfDoc.text(formatCurrency(gasto.iva), pageWidth - margin, y, { align: 'right' });
  y += 10;

  // Total destacado
  jsPdfDoc.setDrawColor(0, 0, 0);
  jsPdfDoc.line(pageWidth - 80, y, pageWidth - margin, y);
  y += 8;
  jsPdfDoc.setFont('helvetica', 'bold');
  jsPdfDoc.setFontSize(16);
  jsPdfDoc.text('TOTAL:', margin, y);
  jsPdfDoc.text(formatCurrency(gasto.total), pageWidth - margin, y, { align: 'right' });
  y += 20;

  // Información adicional
  if (gasto.origen === 'IA') {
    jsPdfDoc.setFontSize(10);
    jsPdfDoc.setFont('helvetica', 'italic');
    jsPdfDoc.setTextColor(100, 100, 100);
    jsPdfDoc.text('Este gasto fue creado automáticamente mediante procesamiento de imagen con IA.', margin, y);
    jsPdfDoc.setTextColor(0, 0, 0);
  }

  // Si hay documento original, anexarlo
  if (gasto.archivoOriginal) {
    const finalPdfBytes = await appendOriginalDocument(jsPdfDoc, gasto.archivoOriginal);
    downloadBlob(finalPdfBytes, filename);
  } else {
    jsPdfDoc.save(filename);
  }
}
